import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import { formatCurrency } from 'tio-ui/format';
import Chip from 'tio-ui/components/chip';
import Button from 'tio-ui/components/button';
import NavCard from 'tio-ui/components/nav-card';
import {
  PhGraduationCap,
  PhBank,
  PhSparkle,
  PhArrowRight,
  PhClock,
  PhBookOpen,
  PhCertificate,
  PhLightbulb,
  PhRocket,
  PhChartPie,
  PhHandshake,
  PhShieldCheck,
  PhFileText,
  PhPlus,
  PhBuildings,
  PhFlag,
  PhCoins,
} from 'ember-phosphor-icons';
import {
  getCurrentDemoClientData,
  getCurrentDemoScenario,
  getCurrentDemoClientId,
} from 'tio-employee/utils/career-pathways-demo-data';
import type CareerPathwaysApiService from 'tio-employee/services/career-pathways-api';
import type { LrapDiscoverySummary } from 'tio-employee/services/career-pathways-api';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type SessionContextService from 'tio-employee/services/session-context';
import type {
  LiveTasData,
  LivePslfData,
  LiveSection127Info,
  DataSourceInfo,
} from 'tio-employee/services/reimagined-data';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import PageHero from 'tio-ui/components/page-hero';

interface BenefitsModel {
  benefitsSummary: {
    section127: LiveSection127Info;
    slr: {
      enrolled: boolean;
      yearToDate: number;
      lifetime: number;
      isLiveData: boolean;
    };
    tas: {
      enrolled: boolean;
      totalApproved: number;
      totalPaid: number;
      pendingCount: number;
      activeCount: number;
      completedCount: number;
      isLiveData: boolean;
    };
    pslf: {
      enrolled: boolean;
      qualifyingPayments: number;
      estimatedForgiveness: number;
      isLiveData: boolean;
    };
    hasAnyBenefits: boolean;
  };
  tasData: LiveTasData;
  pslfData: LivePslfData;
  employerContributions: {
    contributions: { yearToDate: number; lifetime: number; projectedAnnual: number };
    isLiveData: boolean;
  };
  section127Info: LiveSection127Info;
  dataSourceInfo: DataSourceInfo;
}

interface BenefitsIndexSignature {
  Args: {
    model: BenefitsModel;
  };
}

// Demo benefit program data
interface BenefitProgram {
  id: string;
  name: string;
  type: 'tuition' | 'certification' | 'student-loan' | 'dependent';
  status: 'active' | 'pending' | 'completed';
  annualMax: number;
  used: number;
  remaining: number;
  description: string;
  icon: 'graduation' | 'certificate' | 'bank' | 'book';
}

interface ActiveApplication {
  id: string;
  programName: string;
  courseName: string;
  provider: string;
  amount: number;
  status: 'pending-approval' | 'approved' | 'in-progress' | 'completed';
  submittedDate: string;
  expectedCompletion?: string;
}

const DEMO_PROGRAMS: BenefitProgram[] = [
  {
    id: '1',
    name: 'Tuition Assistance',
    type: 'tuition',
    status: 'active',
    annualMax: 5250,
    used: 3200,
    remaining: 2050,
    description: 'Tax-free education benefit for degree programs',
    icon: 'graduation',
  },
  {
    id: '2',
    name: 'Professional Certifications',
    type: 'certification',
    status: 'active',
    annualMax: 2500,
    used: 800,
    remaining: 1700,
    description: 'Industry certifications and licenses',
    icon: 'certificate',
  },
  {
    id: '3',
    name: 'Student Loan Repayment',
    type: 'student-loan',
    status: 'active',
    annualMax: 1500,
    used: 1500,
    remaining: 0,
    description: 'Direct payments to your student loans',
    icon: 'bank',
  },
  {
    id: '4',
    name: 'Dependent Education',
    type: 'dependent',
    status: 'active',
    annualMax: 2000,
    used: 0,
    remaining: 2000,
    description: '529 contributions for dependents',
    icon: 'book',
  },
];

const DEMO_APPLICATIONS: ActiveApplication[] = [
  {
    id: '1',
    programName: 'Tuition Assistance',
    courseName: 'Data Analytics Certificate',
    provider: 'Coursera / Google',
    amount: 299,
    status: 'approved',
    submittedDate: '2026-01-15',
    expectedCompletion: '2026-04-30',
  },
  {
    id: '2',
    programName: 'Professional Certifications',
    courseName: 'AWS Solutions Architect',
    provider: 'Amazon Web Services',
    amount: 450,
    status: 'pending-approval',
    submittedDate: '2026-01-28',
  },
];

export default class CareerPathwaysBenefitsIndex extends Component<BenefitsIndexSignature> {
  @tracked selectedTab = 'overview';
  @service('career-pathways-api') declare careerPathwaysApi: CareerPathwaysApiService;
  @service declare reimaginedData: ReimaginedDataService;
  @service declare sessionContext: SessionContextService;

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  /**
   * Benefits data source based on whether we have live TAS data
   */
  get benefitsSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.args.model.tasData.isLiveData ? 'live' : 'placeholder';
  }

  get lrapSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return 'placeholder';
  }

  get demoClient() {
    return getCurrentDemoClientData();
  }

  get scenario() {
    return getCurrentDemoScenario();
  }

  get employerName() {
    if (this.isDemoMode) return this.demoClient.client.name;
    return (
      this.sessionContext.company?.name ||
      this.sessionContext.currentEmployee?.company?.name ||
      'Your employer'
    );
  }

  get showTuitionAssistance() {
    if (this.isDemoMode) return this.scenario.features.tuitionAssistance;
    return this.args.model.tasData.isEligible;
  }

  get showStudentLoans() {
    if (this.isDemoMode) return this.scenario.features.studentLoanRepayment;
    return this.args.model.benefitsSummary.slr.enrolled;
  }

  get isFirstLogin() {
    return this.scenario.userState.isFirstLogin;
  }

  get programs() {
    // If we have live TAS data, build programs from it
    if (!this.isDemoMode && this.args.model.tasData.isLiveData) {
      return this.buildLivePrograms();
    }

    // Fall back to demo programs filtered by scenario
    return DEMO_PROGRAMS.filter((p) => {
      if (p.type === 'student-loan' && !this.showStudentLoans) return false;
      if (['tuition', 'certification', 'dependent'].includes(p.type) && !this.showTuitionAssistance)
        return false;
      return true;
    });
  }

  private buildLivePrograms(): BenefitProgram[] {
    const programs: BenefitProgram[] = [];
    const { tasData, benefitsSummary, employerContributions } = this.args.model;

    // Tuition Assistance program
    if (tasData.isEligible) {
      const tasUsed = benefitsSummary.tas.totalApproved;
      const annualMax = benefitsSummary.section127.annualLimit;
      programs.push({
        id: 'tas',
        name: 'Tuition Assistance',
        type: 'tuition',
        status: 'active',
        annualMax,
        used: tasUsed,
        remaining: Math.max(0, annualMax - tasUsed),
        description: 'Tax-free education benefit for degree programs',
        icon: 'graduation',
      });
    }

    // Student Loan Repayment program
    if (benefitsSummary.slr.enrolled) {
      programs.push({
        id: 'slr',
        name: 'Student Loan Repayment',
        type: 'student-loan',
        status: 'active',
        annualMax: benefitsSummary.section127.annualLimit,
        used: employerContributions.contributions.yearToDate,
        remaining: Math.max(
          0,
          benefitsSummary.section127.annualLimit - employerContributions.contributions.yearToDate
        ),
        description: 'Direct payments to your student loans',
        icon: 'bank',
      });
    }

    return programs;
  }

  get applications() {
    // If we have live TAS data, use it
    if (!this.isDemoMode && this.args.model.tasData.isLiveData) {
      return this.args.model.tasData.applications.map((app) => ({
        id: app.id,
        programName: app.programName || 'Tuition Assistance',
        courseName: app.name,
        provider: 'Institution',
        amount: app.approvedTotal || 0,
        status: this.mapTasStateToStatus(app.state),
        submittedDate: app.createdAt,
        expectedCompletion: app.coursesEndDate || undefined,
      }));
    }
    return DEMO_APPLICATIONS;
  }

  private mapTasStateToStatus(state: string): ActiveApplication['status'] {
    if (state.includes('PENDING_COURSES_APPROVAL') || state.includes('PENDING_EVIDENCE_APPROVAL')) {
      return 'pending-approval';
    }
    if (state.includes('COURSES_APPROVED') || state.includes('EVIDENCE_APPROVED')) {
      return 'approved';
    }
    if (state.includes('ATTEND')) {
      return 'in-progress';
    }
    if (state.includes('FULFILLED')) {
      return 'completed';
    }
    return 'pending-approval';
  }

  get totalAnnualBudget() {
    if (!this.isDemoMode && this.args.model.section127Info.isLiveData) {
      return this.args.model.section127Info.annualLimit;
    }
    return this.programs.reduce((sum, p) => sum + p.annualMax, 0);
  }

  get totalUsed() {
    if (!this.isDemoMode && this.args.model.section127Info.isLiveData) {
      return this.args.model.section127Info.yearToDateUsed;
    }
    return this.programs.reduce((sum, p) => sum + p.used, 0);
  }

  get totalRemaining() {
    if (!this.isDemoMode && this.args.model.section127Info.isLiveData) {
      return this.args.model.section127Info.remaining;
    }
    return this.programs.reduce((sum, p) => sum + p.remaining, 0);
  }

  get utilizationPercent() {
    return Math.round((this.totalUsed / this.totalAnnualBudget) * 100);
  }

  get utilizationStyle() {
    return htmlSafe(`width: ${this.utilizationPercent}%`);
  }

  get daysLeftInYear() {
    const now = new Date();
    const endOfYear = new Date(now.getFullYear(), 11, 31);
    const diff = endOfYear.getTime() - now.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  }

  get lrapDiscovery(): LrapDiscoverySummary | null {
    const clientId = getCurrentDemoClientId();
    if (clientId === 'legal' || clientId === 'advocate') {
      return this.careerPathwaysApi.fetchLrapDiscoverySyncForDemo();
    }
    return null;
  }

  get hasLrapOpportunity() {
    const discovery = this.lrapDiscovery;
    if (!discovery) return false;
    return (
      discovery.eligiblePrograms.length > 0 ||
      discovery.enrolledPrograms.length > 0 ||
      discovery.totalPotentialAnnualValue > 0
    );
  }

  get lrapEnrolledTotal() {
    return this.lrapDiscovery?.enrolledPrograms.reduce((sum, e) => sum + e.annualBenefit, 0) ?? 0;
  }

  get lrapEligibleTotal() {
    return (
      this.lrapDiscovery?.eligiblePrograms.reduce((sum, e) => sum + e.estimatedAnnualValue, 0) ?? 0
    );
  }

  get totalAvailableAssistance() {
    // Employer benefits + LRAP enrolled + LRAP eligible (potential)
    return this.totalAnnualBudget + this.lrapEnrolledTotal + this.lrapEligibleTotal;
  }

  get assistanceSources() {
    const sources: Array<{
      name: string;
      type: 'employer' | 'school' | 'state' | 'federal';
      amount: number;
      status: 'active' | 'eligible' | 'potential';
    }> = [];

    sources.push({
      name: `${this.demoClient.client.name} Education Benefits`,
      type: 'employer',
      amount: this.totalAnnualBudget,
      status: 'active',
    });

    this.lrapDiscovery?.enrolledPrograms.forEach((enrollment) => {
      sources.push({
        name: enrollment.programName,
        type: enrollment.programType as 'school' | 'state' | 'federal',
        amount: enrollment.annualBenefit,
        status: 'active',
      });
    });

    this.lrapDiscovery?.eligiblePrograms.forEach((eligibility) => {
      sources.push({
        name: eligibility.program.name,
        type: eligibility.program.type as 'school' | 'state' | 'federal',
        amount: eligibility.estimatedAnnualValue,
        status: eligibility.status === 'eligible' ? 'eligible' : 'potential',
      });
    });

    return sources;
  }

  getStatusIntent = (status: ActiveApplication['status']) => {
    const map = {
      'pending-approval': 'warning' as const,
      approved: 'success' as const,
      'in-progress': 'primary' as const,
      completed: 'default' as const,
    };
    return map[status];
  };

  getStatusLabel = (status: ActiveApplication['status']) => {
    const map = {
      'pending-approval': 'Pending Approval',
      approved: 'Approved',
      'in-progress': 'In Progress',
      completed: 'Completed',
    };
    return map[status];
  };

  getProgramIcon = (icon: BenefitProgram['icon']) => {
    const map = {
      graduation: PhGraduationCap,
      certificate: PhCertificate,
      bank: PhBank,
      book: PhBookOpen,
    };
    return map[icon];
  };

  getProgramUtilization = (program: BenefitProgram) => {
    return Math.round((program.used / program.annualMax) * 100);
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="data-audit w-full min-w-0">
      <DataIndicator
        @source={{this.benefitsSource}}
        @showBadge={{true}}
        @dataSourceId="tas-eligibility"
      >
        <PageHero @gradient="hero" class="tio-benefits-hero">
          <:default>
            <div class="tio-benefits-hero__overview">
              <div class="tio-benefits-hero__identity">
                <div class="tio-benefits-hero__heading">
                  <div class="tio-benefits-hero__icon">
                    <PhGraduationCap @size={{28}} weight="duotone" />
                  </div>
                  <div>
                    <Heading @level={{1}} class="tio-fg-inverse">Education Benefits Hub</Heading>
                    <Text @variant="inherit" class="tio-benefits-hero__powered-by">
                      Powered by
                      {{this.demoClient.client.name}}
                    </Text>
                  </div>
                </div>
                <Text @variant="inherit" @size="lg" class="tio-benefits-hero__description">
                  Your employer invests in your growth. You have
                  <span class="font-bold">{{formatCurrency this.totalRemaining}}</span>
                  available to invest in your education this year.
                </Text>
              </div>
              <div class="tio-benefits-hero__summary">
                <div class="tio-benefits-hero__summary-label">{{this.daysLeftInYear}}
                  days left in 2026</div>
                <div class="tio-benefits-hero__summary-value tio-text-4xl-bold">
                  {{this.utilizationPercent}}%
                </div>
                <Text @variant="inherit" class="tio-benefits-hero__summary-caption">
                  benefits used
                </Text>
              </div>
            </div>

            <div class="mt-6">
              <div class="tio-flex-between text-sm mb-2">
                <span>{{formatCurrency this.totalUsed}} used</span>
                <span>{{formatCurrency this.totalRemaining}} remaining</span>
              </div>
              <div class="tio-benefits-hero__progress-track">
                <div
                  class="h-full tio-gradient-info rounded-full transition-all"
                  style={{this.utilizationStyle}}
                ></div>
              </div>
            </div>
          </:default>
        </PageHero>
      </DataIndicator>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        {{#if this.showTuitionAssistance}}
          <NavCard
            @layout="horizontal"
            @route="authenticated.tas.dashboard"
            @icon={{PhPlus}}
            @title="Start New Application"
            @subtitle="Request education funding"
            @intent="info"
          />

          <NavCard
            @layout="horizontal"
            @route="authenticated.tas.dashboard"
            @icon={{PhFileText}}
            @title="My Applications"
            @subtitle="Track your requests"
            @intent="brand"
          />
        {{/if}}

        <NavCard
          @layout="horizontal"
          @route="authenticated.reimagined.explore"
          @icon={{PhRocket}}
          @title="Explore Programs"
          @subtitle="Find learning opportunities"
          @intent="success"
        />
      </div>

      {{#if this.hasLrapOpportunity}}
        <DataIndicator
          @source={{this.lrapSource}}
          @showBadge={{true}}
          @dataSourceId="lrap-opportunity"
        >
          <div class="mt-6 tio-gradient-info-soft border tio-border-info rounded-xl p-6">
            <div class="tio-row-between mb-4">
              <div class="tio-row">
                <div class="p-3 tio-bg-info-soft rounded-xl">
                  <PhCoins @size={{28}} class="tio-fg-info" />
                </div>
                <div>
                  <Heading @level={{2}} @size="xl" class="font-bold tio-fg-info-h">
                    Total Available Assistance
                  </Heading>
                  <Text class="tio-fg-info-body">All sources combined for your education & debt
                    goals</Text>
                </div>
              </div>
              <div class="text-right">
                <Text @weight="bold" class="text-4xl tio-fg-info-body">
                  {{formatCurrency this.totalAvailableAssistance}}
                </Text>
                <Text @variant="muted">/year potential</Text>
              </div>
            </div>

            <div class="tio-bg-surface rounded-xl p-4 mt-4">
              <div class="tio-row-tight mb-3">
                <PhChartPie @size={{20}} class="tio-fg-muted" />
                <Text @weight="semibold" class="tio-fg-secondary">Assistance by Source</Text>
              </div>
              <div class="divide-y tio-divide-subtle">
                {{#each this.assistanceSources as |source|}}
                  <div class="py-3 tio-row-between">
                    <div class="tio-row">
                      <div
                        class="p-2 rounded-lg
                          {{if
                            (isEqual source.type 'employer')
                            'tio-bg-brand-soft tio-fg-brand'
                            (if
                              (isEqual source.type 'school')
                              'tio-bg-info-soft tio-fg-info'
                              (if
                                (isEqual source.type 'state')
                                'tio-bg-warning-soft tio-fg-warning'
                                'tio-bg-success-soft tio-fg-success'
                              )
                            )
                          }}"
                      >
                        {{#if (isEqual source.type "employer")}}
                          <PhHandshake @size={{18}} />
                        {{else if (isEqual source.type "school")}}
                          <PhGraduationCap @size={{18}} />
                        {{else if (isEqual source.type "state")}}
                          <PhFlag @size={{18}} />
                        {{else}}
                          <PhBuildings @size={{18}} />
                        {{/if}}
                      </div>
                      <div>
                        <Text @weight="medium">{{source.name}}</Text>
                        <Text @variant="muted" @size="xs" class="capitalize">{{source.type}}</Text>
                      </div>
                    </div>
                    <div class="tio-row">
                      <Text
                        class="font-bold
                          {{if
                            (isEqual source.status 'active')
                            'tio-fg-success'
                            (if (isEqual source.status 'eligible') 'tio-fg-info' 'tio-fg-muted')
                          }}"
                      >
                        {{formatCurrency source.amount}}/yr
                      </Text>
                      <Chip
                        @intent={{if
                          (isEqual source.status "active")
                          "success"
                          (if (isEqual source.status "eligible") "primary" "default")
                        }}
                        @size="sm"
                      >
                        {{#if (isEqual source.status "active")}}Active{{/if}}
                        {{#if (isEqual source.status "eligible")}}Eligible{{/if}}
                        {{#if (isEqual source.status "potential")}}Potential{{/if}}
                      </Chip>
                    </div>
                  </div>
                {{/each}}
              </div>
            </div>

            <div class="tio-row mt-4">
              <Button
                @route="authenticated.reimagined.finance.optimizer"
                @intent="default"
                @size="sm"
                @appearance="outlined"
              >
                <PhSparkle @size={{16}} class="mr-1" />
                Optimize Allocation
              </Button>
            </div>
          </div>
        </DataIndicator>
      {{/if}}

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <div class="lg:col-span-2">
          <div class="tio-row-between mb-4">
            <div class="tio-row-tight">
              <PhChartPie @size={{24}} class="tio-fg-secondary" />
              <Heading @level={{2}} @size="lg">Your Benefit Programs</Heading>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            {{#each this.programs as |program|}}
              <div
                class="tio-bg-surface rounded-xl p-5 tio-hairline-card tio-hairline-card--interactive"
                data-test-benefit-program-card
              >
                <div class="tio-row-start-between mb-3">
                  <div class="tio-row">
                    {{#let (this.getProgramIcon program.icon) as |Icon|}}
                      <div
                        class="p-2.5 rounded-lg
                          {{if
                            (isEqual program.type 'tuition')
                            'tio-bg-info-soft tio-fg-info'
                            (if
                              (isEqual program.type 'certification')
                              'tio-bg-warning-soft tio-fg-warning'
                              (if
                                (isEqual program.type 'student-loan')
                                'tio-bg-success-soft tio-fg-success'
                                'tio-bg-brand-soft tio-fg-brand'
                              )
                            )
                          }}"
                      >
                        <Icon @size={{22}} />
                      </div>
                    {{/let}}
                    <div>
                      <div class="font-semibold tio-fg-primary">{{program.name}}</div>
                      <Text @variant="muted" @size="xs">{{program.description}}</Text>
                    </div>
                  </div>
                </div>

                <div class="mb-3">
                  <div class="tio-flex-between text-sm mb-1">
                    <span class="tio-fg-muted">{{formatCurrency program.used}} used</span>
                    <span class="font-medium tio-fg-primary">{{formatCurrency program.remaining}}
                      left</span>
                  </div>
                  <div class="h-2 tio-bg-sunken rounded-full overflow-hidden">
                    <div
                      class="h-full rounded-full transition-all
                        {{if
                          (isEqual program.type 'tuition')
                          'tio-bg-info'
                          (if
                            (isEqual program.type 'certification')
                            'tio-bg-warning'
                            (if
                              (isEqual program.type 'student-loan') 'tio-bg-success' 'tio-bg-brand'
                            )
                          )
                        }}"
                      style={{htmlSafe (concat "width: " (this.getProgramUtilization program) "%")}}
                    ></div>
                  </div>
                </div>

                <div class="tio-row-between pt-3 border-t tio-border-subtle">
                  <Text @variant="muted" @size="sm">
                    {{formatCurrency program.annualMax}}/year max
                  </Text>
                  {{#if (isGreater program.remaining 0)}}
                    <LinkTo
                      @route="authenticated.tas.dashboard"
                      class="tio-row-extra-tight tio-text-sm-medium app-text-link"
                    >
                      <span>Apply</span>
                      <PhArrowRight @size={{14}} />
                    </LinkTo>
                  {{else}}
                    <Chip @intent="success">Fully Utilized</Chip>
                  {{/if}}
                </div>
              </div>
            {{/each}}
          </div>
        </div>

        {{#if this.showTuitionAssistance}}
          <div>
            <div class="tio-row-tight mb-4">
              <PhClock @size={{24}} class="tio-fg-secondary" />
              <Heading @level={{2}} @size="lg">Active Applications</Heading>
            </div>

            <div class="space-y-3">
              {{#each this.applications as |app|}}
                <div
                  class="tio-bg-surface rounded-xl p-4 tio-hairline-card"
                  data-test-benefit-application-card
                >
                  <div class="tio-row-start-between mb-2">
                    <div class="flex-1">
                      <div class="font-medium tio-fg-primary">{{app.courseName}}</div>
                      <Text @variant="muted" @size="sm">{{app.provider}}</Text>
                    </div>
                    <Chip @intent={{this.getStatusIntent app.status}}>
                      {{this.getStatusLabel app.status}}
                    </Chip>
                  </div>
                  <div class="tio-row-between text-sm">
                    <span class="tio-fg-muted">{{app.programName}}</span>
                    <span class="font-semibold tio-fg-primary">{{formatCurrency app.amount}}</span>
                  </div>
                </div>
              {{/each}}

              {{#if (isEqual this.applications.length 0)}}
                <div class="tio-bg-sunken rounded-xl p-6 text-center">
                  <PhFileText @size={{32}} class="tio-fg-muted mx-auto mb-2" />
                  <Text @variant="muted">No active applications</Text>
                  <LinkTo
                    @route="authenticated.tas.dashboard"
                    class="tio-inline-row gap-1 mt-2 tio-text-sm-medium app-text-link"
                  >
                    <span>Start one now</span>
                    <PhArrowRight @size={{14}} />
                  </LinkTo>
                </div>
              {{/if}}
            </div>

            <LinkTo
              @route="authenticated.tas.dashboard"
              class="tio-row-tight justify-center mt-4 py-2 tio-text-sm-medium app-text-link"
            >
              <span>View All Applications</span>
              <PhArrowRight @size={{16}} />
            </LinkTo>
          </div>
        {{/if}}
      </div>

      {{#if this.showTuitionAssistance}}
        <div class="tio-callout tio-callout--warning tio-callout--gradient mt-6">
          <div class="tio-callout__icon">
            <PhLightbulb @size={{28}} />
          </div>
          <div class="flex-1">
            <h3 class="tio-callout__title">AI Recommendation</h3>
            <p class="mb-4">
              Based on your career goals and current role, consider the
              <span class="font-semibold">AWS Solutions Architect certification</span>. It aligns
              with your interest in cloud technologies and has a 94% completion rate among
              {{this.demoClient.client.name}}
              employees. You have $1,700 available in your certification budget.
            </p>
            <div class="tio-wrap-tight">
              <Chip @intent="warning">High Career Impact</Chip>
              <Chip @intent="default">12-week program</Chip>
              <Chip @intent="default">$450 cost</Chip>
            </div>
          </div>
          <Button
            @route="authenticated.reimagined.plan.new"
            @intent="primary"
            @appearance="outlined"
            @icon={{PhArrowRight}}
            @iconPlacement="end"
            @label="Add to Plan"
          />
        </div>
      {{/if}}

      <div class="tio-callout tio-callout--success tio-callout--gradient mt-6 items-center">
        <div class="tio-callout__icon">
          <PhShieldCheck @size={{28}} />
        </div>
        <div class="flex-1">
          <h3 class="tio-callout__title">Section 127 Tax Savings</h3>
          <p>
            You've saved an estimated
            <span class="font-bold">$800</span>
            in taxes this year through your education benefits. The first $5,250 is tax-free!
          </p>
        </div>
        <Button
          @route="authenticated.reimagined.finance"
          @intent="primary"
          @appearance="outlined"
          @icon={{PhArrowRight}}
          @iconPlacement="end"
          @label="View Tax Details"
        />
      </div>
    </VStack>
  </template>
}

const isEqual = (a: unknown, b: unknown) => a === b;
const isGreater = (a: number, b: number) => a > b;
const concat = (...args: (string | number)[]) => args.join('');
